import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { currentWeek } from "@/lib/week";
import { DatumVeld, geldigeDatum, teVroeg, vandaagNL, TE_VROEG } from "@/components/DatumVeld";
import { KAART } from "@/components/rust/Rust";

const LBL = "block text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1";
const VELD = "w-full min-h-[44px] px-3 py-2 rounded-md border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-600";
const LEEG = { wat: "", afdeling: "", locatie: "", deadline: "", peil: [], nieuwPeil: "", werkbon: "" };

function Peilmomenten({ f, set }) {
  const voegToe = () => {
    if (!geldigeDatum(f.nieuwPeil) || teVroeg(f.nieuwPeil, "") || f.peil.includes(f.nieuwPeil)) return;
    set({ peil: [...f.peil, f.nieuwPeil].sort(), nieuwPeil: "" });
  };
  return (
    <div>
      <span className={LBL}>Peilmomenten (optioneel)</span>
      {f.peil.length > 0 && <ul className="flex flex-wrap gap-1.5 mb-2" data-testid="afd-nieuw-peil-lijst">
        {f.peil.map((d) => (
          <li key={d} className="flex items-center gap-1 px-2 py-1 rounded-full bg-zinc-100 border border-zinc-200 font-mono text-xs">
            {d}<button type="button" aria-label={`Peilmoment ${d} weghalen`} data-testid={`afd-nieuw-peil-weg-${d}`} onClick={() => set({ peil: f.peil.filter((x) => x !== d) })}><X className="h-3 w-3" /></button>
          </li>))}
      </ul>}
      <div className="flex gap-2">
        <DatumVeld testid="afd-nieuw-peil-datum" value={f.nieuwPeil} onChange={(v) => set({ nieuwPeil: v })} min={vandaagNL()} aria-label="Peilmoment toevoegen" />
        <button type="button" data-testid="afd-nieuw-peil-toevoegen" onClick={voegToe} className="shrink-0 px-3 rounded-md border border-zinc-300 text-sm font-semibold hover:bg-zinc-50">Toevoegen</button>
      </div>
      {f.nieuwPeil && teVroeg(f.nieuwPeil, "") && <p className="text-xs font-semibold text-rose-600 mt-1">{TE_VROEG}</p>}
    </div>
  );
}

// v20 A: een afdeling maakt zelf een afspraak voor de eigen afdeling. Afronden doen de OM of oversight.
export const NieuweAfdelingAfspraak = ({ meta, onToegevoegd }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(LEEG);
  const [bezig, setBezig] = useState(false);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const eigen = (meta?.eigenAfdelingen || []).filter((x) => x.actief !== false);   // v20-R1 R16: alleen actieve
  const locaties = meta?.afspraakLocaties || [];
  const opslaan = async () => {
    if (!f.wat.trim()) { toast.error("Vul in wat er moet gebeuren."); return; }
    if (eigen.length > 1 && !f.afdeling) { toast.error("Kies de afdeling."); return; }
    if (f.deadline && (!geldigeDatum(f.deadline) || teVroeg(f.deadline, ""))) { toast.error(`Deadline: ${TE_VROEG}`); return; }
    setBezig(true);
    try {
      const r = await api.post("/afspraken", {
        week: currentWeek(), wat: f.wat.trim(), afdeling: f.afdeling || (eigen[0]?.id ?? null), locatie: f.locatie || null,
        deadline: f.deadline || null, werkbon: f.werkbon.trim() || null, checkpoints: f.peil.map((date) => ({ date, wat: "" })),
      });
      toast.success("Afspraak toegevoegd");
      setF(LEEG); setOpen(false); onToegevoegd(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt. Je tekst staat nog in het formulier."); }
    finally { setBezig(false); }
  };
  if (!open) return (
    <button type="button" data-testid="afdeling-nieuwe-afspraak" onClick={() => setOpen(true)}
      className="mb-3 inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-md bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors">
      <Plus className="h-4 w-4" /> Nieuwe afspraak
    </button>
  );
  return (
    <div data-testid="afd-nieuw-form" className={`${KAART} mb-4 p-4 space-y-3`}>
      <p className="text-sm font-semibold text-zinc-900">Nieuwe afspraak voor je afdeling</p>
      <label className="block"><span className={LBL}>Wat</span>
        <textarea data-testid="afd-nieuw-wat" rows={2} value={f.wat} onChange={(e) => set({ wat: e.target.value })} className={VELD} placeholder="Wat moet er gebeuren?" /></label>
      {eigen.length > 1 && <label className="block"><span className={LBL}>Afdeling</span>
        <select data-testid="afd-nieuw-afdeling" value={f.afdeling} onChange={(e) => set({ afdeling: e.target.value })} className={VELD}>
          <option value="">Kies de afdeling</option>
          {eigen.map((a) => <option key={a.id} value={a.id}>{a.naam}</option>)}
        </select></label>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block"><span className={LBL}>Locatie</span>
          <select data-testid="afd-nieuw-locatie" value={f.locatie} onChange={(e) => set({ locatie: e.target.value })} className={VELD}>
            <option value="">Algemeen / geen locatie</option>
            {locaties.map((l) => <option key={l} value={l}>{l}</option>)}
          </select></label>
        <label className="block"><span className={LBL}>Deadline</span>
          <DatumVeld testid="afd-nieuw-deadline" value={f.deadline} onChange={(v) => set({ deadline: v })} min={vandaagNL()} aria-label="Deadline" />
          {f.deadline && teVroeg(f.deadline, "") && <p className="text-xs font-semibold text-rose-600 mt-1">{TE_VROEG}</p>}</label>
      </div>
      <Peilmomenten f={f} set={set} />
      <label className="block"><span className={LBL}>Werkbon (optioneel)</span>
        <input data-testid="afd-nieuw-werkbon" maxLength={40} value={f.werkbon} onChange={(e) => set({ werkbon: e.target.value })} placeholder="bijv. TD-2214" className={`${VELD} font-mono`} /></label>
      <p className="text-xs text-zinc-500">De OM en oversight zien de afspraak meteen. Afronden doen zij.</p>
      <div className="flex gap-2 justify-end">
        <button type="button" data-testid="afd-nieuw-annuleren" onClick={() => setOpen(false)} className="min-h-[44px] px-4 rounded-md border border-zinc-300 text-sm font-semibold hover:bg-zinc-50">Annuleren</button>
        <button type="button" data-testid="afd-nieuw-opslaan" disabled={bezig} onClick={opslaan} className="min-h-[44px] px-4 rounded-md bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50">Opslaan</button>
      </div>
    </div>
  );
};
